import React, { useEffect, useRef, useState } from 'react';
import * as OziraAppCore from 'ozira-app-core';
import { OziraMark, buildProductHandoffUrl } from 'ozira-shell-kit';
import { IconEdit, IconPlus, IconSearch, IconSidebar, IconWorkspaceChat, IconWorkspaceCode, IconWorkspaceDesign, IconWorkspaceForward, IconWorkspaceP } from './icons';
import { AccountFooter } from './AccountFooter';
import { ConversationActions, type ConversationAction } from './ConversationActions';
import { conversationWorkspaceStrings } from '../../i18n/conversationWorkspace';
import { useTheme } from '../../theme/ThemeProvider';
import { getConfig } from '../../env';
import { reloadWorkspace } from '../../lib/workspaceLocation';
import type { ConversationSummary, ProjectSummary } from '../../lib/conversationsApi';

const WORKSPACE_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  assistant: IconWorkspaceChat,
  code: IconWorkspaceCode,
  design: IconWorkspaceDesign,
  forward: IconWorkspaceForward,
  p: IconWorkspaceP,
};

interface ConversationSidebarProps {
  conversations?: ConversationSummary[];
  onConversationAction?: (conversation: ConversationSummary, action: ConversationAction) => void | Promise<void>;
  projects?: ProjectSummary[];
  organizationTenantId: string | null;
  activeConversationId?: string | null;
  showArchived?: boolean;
  onSelect?: (id: string) => void;
  onNewChat?: () => void;
  onSearch?: (query: string) => void;
  onToggleArchived?: () => void;
  onNewProject?: () => void;
  onSelectProject?: (projectId: string) => void;
  activeProjectId?: string | null;
  onMobileClose?: () => void;
  onMobileCloseWithoutRestore?: () => void;
  mobileOpen?: boolean;
  onOpenSettings?: () => void;
  activeSection?: 'home' | 'assistant' | 'activity' | 'files';
  onNavigate?: (section: 'home' | 'assistant' | 'activity' | 'files') => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  // Which of the five OZIRA products this sidebar instance is mounted inside
  // -- decides which workspace entry shows as current, and (below) whether
  // the chat-specific New Chat/search/conversations/Projects sections render
  // at all. Defaults to 'assistant' so the original Assistant call site
  // (which never set this) keeps its exact prior behavior.
  activeProductId?: string;
  // When set, clicking another workspace entry updates the URL and re-renders
  // in place (App.tsx's navigateToProduct) instead of the plain <a href>
  // cross-domain handoff link every product used before this switch.
  onNavigateProduct?: (productId: string) => void;
  // Non-Assistant products have no conversations/Projects concept -- this
  // renders in that exact slot instead (a real "recent files" list, or an
  // honest empty state; never a fabricated control).
  recentSlot?: React.ReactNode;
}

export function ConversationSidebar({
  conversations = [],
  onConversationAction,
  projects = [],
  organizationTenantId,
  activeConversationId = null,
  showArchived = false,
  onSelect,
  onNewChat,
  onSearch,
  onToggleArchived,
  onNewProject,
  onSelectProject,
  activeProjectId = null,
  onMobileClose,
  onMobileCloseWithoutRestore,
  mobileOpen = false,
  onOpenSettings,
  activeSection = 'assistant',
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  activeProductId = 'assistant',
  onNavigateProduct,
  recentSlot,
}: ConversationSidebarProps) {
  const { language, mode } = useTheme();
  const t = (key: string) => conversationWorkspaceStrings[language]?.[key] ?? conversationWorkspaceStrings.en[key] ?? key;
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const railToggleRef = useRef<HTMLButtonElement>(null);
  const collapseButtonRef = useRef<HTMLButtonElement>(null);
  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isFirstCollapseRender = useRef(true);
  const isFirstSearchRender = useRef(true);

  // Cross-branch focus management: collapsing/expanding swaps which DOM node
  // exists entirely (rail button vs. header close button), so focus can only
  // move once the new branch has actually mounted -- an effect, not the click
  // handler that toggles `collapsed`, is what fires after that happens. Both
  // effects are declared unconditionally, above the collapsed/expanded
  // branch split below, so they run every render with the same hook count
  // (the ordering hazard that caused this session's #310 outage was a hook
  // called only on one side of an early return, not effects in general).
  useEffect(() => {
    if (isFirstCollapseRender.current) { isFirstCollapseRender.current = false; return; }
    if (collapsed) railToggleRef.current?.focus();
    else collapseButtonRef.current?.focus();
  }, [collapsed]);

  useEffect(() => {
    if (isFirstSearchRender.current) { isFirstSearchRender.current = false; return; }
    if (searchOpen) searchInputRef.current?.focus();
    else searchToggleRef.current?.focus();
  }, [searchOpen]);

  // WebApp.dc.html's "Workspaces" list: a real, always-visible switcher
  // between the five OZIRA products (assistant/code/design/forward/p), not
  // a decorative label. This sidebar only ever renders inside the Assistant
  // product, so "Chat" is always the current entry; the rest are real
  // cross-domain links carrying the same allowlisted handoff context as the
  // OziraProductSwitcher dropdown in the top bar (spec: "Product switch
  // preserves context, not authority" -- switching still re-authenticates).
  // Computed above the collapsed branch below because the icon rail renders
  // the same switcher, icon-only.
  const productUrls = getConfig().productUrls;
  const handoffContext = {
    tenantId: organizationTenantId,
    language,
    theme: mode,
  };
  // All five real products, renamed to the platform's own naming
  // (Chat/Code/Design/Forward/Cowork) rather than their internal ids.
  const workspaceManifests = (OziraAppCore.listManifests() as Array<{ productId: string; displayName: string }>)
    .map((manifest) => {
      if (manifest.productId === 'assistant') return { ...manifest, displayName: 'Chat' };
      if (manifest.productId === 'p') return { ...manifest, displayName: 'Cowork' };
      return manifest;
    });

  const workspaceHref = (productId: string) => {
    const base = productUrls[productId];
    return base ? buildProductHandoffUrl(base, handoffContext) : null;
  };

  if (collapsed && !mobileOpen) {
    return (
      <aside className="ozira-cw-sidebar is-collapsed" id="ozira-conversations-panel" aria-label={t('assistantTitle')}>
        <button
          type="button"
          ref={railToggleRef}
          className="ozira-cw-rail-logo"
          aria-label={t('openSidebar')}
          title={t('openSidebar')}
          aria-expanded={false}
          aria-controls="ozira-conversations-panel"
          onClick={onToggleCollapsed}
        >
          <span className="ozira-cw-rail-logo__mark" aria-hidden="true"><OziraMark size={28} /></span>
          <IconSidebar className="ozira-cw-rail-logo__icon" width={20} height={20} aria-hidden="true" />
        </button>

        <button
          type="button"
          className="ozira-cw-rail-icon"
          aria-label={t('newChat')}
          title={t('newChat')}
          onClick={onNewChat}
        >
          <IconEdit width={18} height={18} aria-hidden="true" />
        </button>

        {/* The same real product switcher the expanded sidebar renders --
            same manifests, same allowlisted handoff URLs -- just icon-only. */}
        <ul className="ozira-cw-rail-workspaces" aria-label={t('workspaces')}>
          {workspaceManifests.map((manifest) => {
            const Icon = WORKSPACE_ICONS[manifest.productId] ?? IconWorkspaceChat;
            if (manifest.productId === activeProductId) {
              return (
                <li key={manifest.productId}>
                  <span className="ozira-cw-rail-icon is-active" aria-current="page" aria-label={manifest.displayName} title={manifest.displayName} role="img">
                    <Icon width={18} height={18} />
                  </span>
                </li>
              );
            }
            if (onNavigateProduct) {
              return (
                <li key={manifest.productId}>
                  <button type="button" className="ozira-cw-rail-icon" onClick={() => onNavigateProduct(manifest.productId)} aria-label={manifest.displayName} title={manifest.displayName}>
                    <Icon width={18} height={18} />
                  </button>
                </li>
              );
            }
            const href = workspaceHref(manifest.productId);
            return (
              <li key={manifest.productId}>
                {href ? (
                  <a className="ozira-cw-rail-icon" href={href} aria-label={manifest.displayName} title={manifest.displayName}>
                    <Icon width={18} height={18} />
                  </a>
                ) : (
                  <span className="ozira-cw-rail-icon is-disabled" aria-disabled="true" aria-label={manifest.displayName} title={manifest.displayName} role="img">
                    <Icon width={18} height={18} />
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {/* Avatar only -- omitting onNewChat drops AccountFooter's own New
            chat button, so the account sits alone at the bottom (its
            margin-top:auto) instead of paired with New chat mid-rail. */}
        <AccountFooter onOpenSettings={onOpenSettings} />
      </aside>
    );
  }

  // Conversations, Projects, search and the archived toggle only mean
  // anything inside Chat -- every other product shows its own recentSlot
  // (a real recent-items list, or an honest empty state) in this exact spot
  // instead, per "the sidebar stays the same, only the recent file changes."
  const isChatWorkspace = activeProductId === 'assistant';
  const pinned = conversations.filter((c) => c.pinned && !c.archived);
  const recent = conversations.filter((c) => !c.pinned && !c.archived);
  const closeAfter = (action: () => void, restoreFocus = true) => {
    action();
    if (mobileOpen) {
      if (restoreFocus) onMobileClose?.();
      else (onMobileCloseWithoutRestore ?? onMobileClose)?.();
    }
  };

  return (
    <aside
      className="ozira-cw-sidebar"
      id="ozira-conversations-panel"
      aria-label={mobileOpen ? 'Conversations' : t('assistantTitle')}
      role={mobileOpen ? 'dialog' : undefined}
      aria-modal={mobileOpen ? true : undefined}
    >
      <button type="button" className="ozira-cw-brand ozira-cw-brand-refresh" aria-label="Refresh current workspace" title="Refresh current workspace" onClick={reloadWorkspace}>
        <OziraMark size={36} />
        <div>
          <strong>OZIRA</strong>
          <span>TRUST BY DESIGN</span>
        </div>
      </button>
      {isChatWorkspace && (
        <nav className="ozira-cw-product-nav" aria-label="OZIRA navigation">
          {(['home', 'assistant', 'activity', 'files'] as const).map((section) => (
            <button
              key={section}
              type="button"
              className={`ozira-cw-product-nav-item${activeSection === section ? ' is-active' : ''}`}
              aria-current={activeSection === section ? 'page' : undefined}
              onClick={() => closeAfter(() => onNavigate?.(section))}
            >
              {section[0].toUpperCase() + section.slice(1)}
            </button>
          ))}
          <button type="button" className="ozira-cw-product-nav-item" onClick={onOpenSettings}>Settings</button>
        </nav>
      )}
      <div className="ozira-cw-search-bar">
        <button
          type="button"
          ref={collapseButtonRef}
          className="ozira-cw-search-bar__collapse"
          aria-label={mobileOpen ? t('closeConversations') : t('closeSidebar')}
          title={mobileOpen ? t('closeConversations') : t('closeSidebar')}
          aria-expanded={mobileOpen ? undefined : true}
          aria-controls={mobileOpen ? undefined : 'ozira-conversations-panel'}
          onClick={mobileOpen ? onMobileClose : onToggleCollapsed}
        >
          <IconSidebar width={18} height={18} aria-hidden="true" />
        </button>
        {isChatWorkspace && (
          <button
            type="button"
            ref={searchToggleRef}
            className="ozira-cw-search-bar__search"
            aria-label={t('searchToggle')}
            title={t('searchToggle')}
            aria-expanded={searchOpen}
            aria-controls="ozira-sidebar-search-form"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <IconSearch width={16} height={16} aria-hidden="true" />
          </button>
        )}
      </div>
      {isChatWorkspace && searchOpen ? (
        <form
          id="ozira-sidebar-search-form"
          role="search"
          className="ozira-cw-search ozira-cw-search--compact"
          onSubmit={(e) => {
            e.preventDefault();
            onSearch?.(query);
          }}
        >
          <IconSearch width={16} height={16} aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="search"
            value={query}
            placeholder={t('searchConversations')}
            aria-label={t('searchConversations')}
            onChange={(e) => {
              setQuery(e.target.value);
              onSearch?.(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setQuery('');
                onSearch?.('');
                setSearchOpen(false);
              }
            }}
          />
        </form>
      ) : null}

      <section className="ozira-cw-section">
        <div className="ozira-cw-section-head">
          <h3>{t('workspaces')}</h3>
        </div>
        <ul className="ozira-cw-list ozira-cw-workspace-list">
          {workspaceManifests.map((manifest) => {
            const Icon = WORKSPACE_ICONS[manifest.productId] ?? IconWorkspaceChat;
            const isCurrent = manifest.productId === activeProductId;
            if (isCurrent) {
              return (
                <li key={manifest.productId}>
                  <span className="ozira-cw-row ozira-cw-workspace-row is-active" aria-current="page">
                    <Icon width={15} height={15} />
                    <span className="ozira-cw-row-title">{manifest.displayName}</span>
                  </span>
                </li>
              );
            }
            if (onNavigateProduct) {
              return (
                <li key={manifest.productId}>
                  <button type="button" className="ozira-cw-row ozira-cw-workspace-row" onClick={() => onNavigateProduct(manifest.productId)}>
                    <Icon width={15} height={15} />
                    <span className="ozira-cw-row-title">{manifest.displayName}</span>
                  </button>
                </li>
              );
            }
            const href = workspaceHref(manifest.productId);
            return (
              <li key={manifest.productId}>
                {href ? (
                  <a className="ozira-cw-row ozira-cw-workspace-row" href={href}>
                    <Icon width={15} height={15} />
                    <span className="ozira-cw-row-title">{manifest.displayName}</span>
                  </a>
                ) : (
                  <span className="ozira-cw-row ozira-cw-workspace-row is-disabled" aria-disabled="true">
                    <Icon width={15} height={15} />
                    <span className="ozira-cw-row-title">{manifest.displayName}</span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {!isChatWorkspace ? (
        recentSlot
      ) : (
      <>
      <section className="ozira-cw-section">
        <div className="ozira-cw-section-head">
          <h3>{t('projects')}</h3>
          <button type="button" className="ozira-cw-icon-inline" aria-label={t('newProject')} title={t('newProject')} onClick={() => closeAfter(() => onNewProject?.(), false)}>
            <IconPlus width={14} height={14} />
          </button>
        </div>
        {projects.length === 0 ? (
          <p className="ozira-cw-empty-hint">{t('noneProject')}</p>
        ) : (
          <ul className="ozira-cw-list">
            {projects.map((project) => (
              <li key={project.id}>
                <button
                  type="button"
                  className={`ozira-cw-row${activeProjectId === project.id ? ' is-active' : ''}`}
                  onClick={() => closeAfter(() => onSelectProject?.(project.id))}
                >
                  <span className="ozira-cw-row-title">{project.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showArchived ? (
        <section className="ozira-cw-section ozira-cw-section--grow">
          <div className="ozira-cw-section-head">
            <h3>{t('archived')}</h3>
          </div>
          {conversations.length === 0 ? (
            <p className="ozira-cw-empty-hint">{t('noConversations')}</p>
          ) : (
            <ul className="ozira-cw-list">
              {conversations.map((conv) => (
                <ConversationRow key={conv.id} conv={conv} active={conv.id === activeConversationId} projects={projects} onAction={onConversationAction} onSelect={(id) => closeAfter(() => onSelect?.(id))} />
              ))}
            </ul>
          )}
        </section>
      ) : (
        <>
          {pinned.length > 0 && (
            <section className="ozira-cw-section">
              <div className="ozira-cw-section-head">
                <h3>{t('pinned')}</h3>
              </div>
              <ul className="ozira-cw-list">
                {pinned.map((conv) => (
                  <ConversationRow key={conv.id} conv={conv} active={conv.id === activeConversationId} projects={projects} onAction={onConversationAction} onSelect={(id) => closeAfter(() => onSelect?.(id))} />
                ))}
              </ul>
            </section>
          )}

          <section className="ozira-cw-section ozira-cw-section--grow">
            <div className="ozira-cw-section-head">
              <h3>{t('recent')}</h3>
            </div>
            {recent.length === 0 ? (
              <p className="ozira-cw-empty-hint">{t('noConversations')}</p>
            ) : (
              <ul className="ozira-cw-list">
                {recent.map((conv) => (
                  <ConversationRow key={conv.id} conv={conv} active={conv.id === activeConversationId} projects={projects} onAction={onConversationAction} onSelect={(id) => closeAfter(() => onSelect?.(id))} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
      </>
      )}

      {isChatWorkspace && (
        <button type="button" className="ozira-cw-archived-link" aria-pressed={showArchived} onClick={onToggleArchived}>
          {t('archived')}
        </button>
      )}

      <AccountFooter onOpenSettings={onOpenSettings} onNewChat={onNewChat ? () => closeAfter(() => onNewChat()) : undefined} newChatLabel={t('newChat')} />
    </aside>
  );
}

function ConversationRow({
  conv,
  active,
  onSelect,
  projects = [],
  onAction,
}: {
  conv: ConversationSummary;
  active: boolean;
  onSelect: (id: string) => void;
  projects?: ProjectSummary[];
  onAction?: (conversation: ConversationSummary, action: ConversationAction) => void | Promise<void>;
}) {
  return (
    <li className="ozira-conversation-row">
      <button type="button" className={`ozira-cw-row${active ? ' is-active' : ''}`} onClick={() => onSelect(conv.id)}>
        <span className="ozira-cw-row-title">{conv.title}</span>
      </button>
      {onAction && <ConversationActions quick conversation={conv} projects={projects} onAction={action => onAction(conv, action)} />}
    </li>
  );
}
